import { LoginResponse } from 'angular-auth-oidc-client';

import { Injectable } from '@angular/core';

import { Observable, catchError, finalize, map, of, switchMap } from 'rxjs';

import { MwAuthService, UserProfileViewModel, AuthModuleConfigId } from '@core/auth';
import { MwHttpService } from '@core/http';
import { MwProgressBarService } from '@core/services';

import { UserLoginViewModel, UserProfileUpdateDto } from './@models';

@Injectable()
export class UserProfileApiService {
  constructor(
    private readonly http: MwHttpService,
    private readonly progressBar: MwProgressBarService,
    private readonly authService: MwAuthService
  ) {}

  hasDevincoConnectAccount(): Observable<boolean> {
    return this.http.authGet<boolean>(
      `/v2/users/login/hasDevincoConnectAccount`,
      { ignoreNotifyStatusCodes: [404] }
    );
  }

  getLogins(): Observable<UserLoginViewModel[]> {
    this.progressBar.show();
    return this.http
      .authGet<UserLoginViewModel[]>(`/v2/users/logins`, {
        ignoreNotifyStatusCodes: [403],
      })
      .pipe(
        finalize(() => this.progressBar.hide()),
        catchError(() => of([]))
      );
  }

  update(dto: UserProfileUpdateDto): Observable<UserProfileViewModel> {
    this.progressBar.show();
    return this.http
      .authPatch<UserProfileViewModel>(`/v2/users/profile`, { body: dto })
      .pipe(finalize(() => this.progressBar.hide()));
  }

  unlink(): Observable<boolean> {
    return this.http
      .authDelete('/v2/users/logins', {
        body: { loginProvider: 'Devinco' },
      })
      .pipe(map(() => true));
  }

  link(): Observable<boolean> {
    return this.authService.checkAuthMultiple().pipe(
      switchMap((loginResponses) => {
        const mwAuthData = this.getLoginData(
          loginResponses,
          AuthModuleConfigId.MobileWorker
        );
        const dcAuthData = this.getLoginData(
          loginResponses,
          AuthModuleConfigId.DevincoConnect
        );

        if (!dcAuthData) {
          throw new Error(
            'Devinco Connect account is not authenticated. Please login first'
          );
        }

        if (!mwAuthData) {
          throw new Error(
            'Mobile Worker account is not authenticated. Please login first'
          );
        }

        const providerKey = dcAuthData.userData.sub;
        const displayName = dcAuthData.userData.name;
        const providerUserName = dcAuthData.userData.username;

        if (!providerKey) {
          throw new Error(
            'Unable to get Subject ID from Devinco Connect authentication data'
          );
        }

        if (!displayName) {
          throw new Error(
            'Unable to get user full name from Devinco Connect authentication data'
          );
        }

        if (!providerUserName) {
          throw new Error(
            'Unable to get user name from Devinco Connect authentication data'
          );
        }

        return this.http.authPost('/v2/users/logins', {
          body: {
            loginProvider: 'Devinco',
            providerKey,
            displayName,
            providerUserName,
          },
          authConfigId: AuthModuleConfigId.MobileWorker,
        });
      }),
      map(() => true)
    );
  }

  private getLoginData(
    loginResponses: LoginResponse[],
    configId: AuthModuleConfigId
  ): LoginResponse {
    return loginResponses.filter((t) => t.configId === configId)[0];
  }
}
